import { Button } from "@rosc/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@rosc/ui/components/dialog";
import { Input } from "@rosc/ui/components/input";
import { startAuthentication } from "@simplewebauthn/browser";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import {
  NativeAuthRequestError,
  type NativeSession,
  nativeAuthRequest,
} from "@/lib/native-auth";

const confirmationSchema = z.object({
  adminStepUpExpiresIn: z.number().int().nonnegative(),
});
const passkeyOptionsSchema = z.object({
  challenge: z.string().min(1),
  rpId: z.string().optional(),
  timeout: z.number().optional(),
  userVerification: z.enum(["required", "preferred", "discouraged"]).optional(),
  allowCredentials: z
    .array(
      z.object({
        id: z.string(),
        type: z.literal("public-key"),
        transports: z
          .array(
            z.enum([
              "ble",
              "cable",
              "hybrid",
              "internal",
              "nfc",
              "smart-card",
              "usb",
            ]),
          )
          .optional(),
      }),
    )
    .optional(),
});

function failureMessage(error: unknown) {
  if (error instanceof NativeAuthRequestError) {
    switch (error.code) {
      case "FORBIDDEN":
        return "Not authorized";
      case "INVALID_CODE":
        return "Invalid authenticator code. Try again.";
      case "REPLAYED_FACTOR":
        return "This code was already used. Wait for a new code and try again.";
      case "ACCOUNT_TEMPORARILY_LOCKED":
        return "Too many attempts. Try again in 15 minutes.";
    }
  }
  return "Confirmation failed. Try again.";
}

export function AdminStepUp({
  session,
  beginConfirmation,
  onConfirmed,
  onForbidden,
  onLeave,
  locking,
  lockError,
}: {
  session: NativeSession;
  beginConfirmation: () => number;
  onConfirmed: (seconds: number, generation: number) => void;
  onForbidden: () => void;
  onLeave: () => void;
  locking: boolean;
  lockError: string | null;
}) {
  const [code, setCode] = useState("");
  const [confirming, setBusy] = useState(false);
  const busy = confirming || locking;
  const [error, setError] = useState<string | null>(null);
  const confirm = async (factor: "totp" | "passkey") => {
    const generation = beginConfirmation();
    setBusy(true);
    setError(null);
    try {
      let result: unknown;
      if (factor === "totp") {
        result = await nativeAuthRequest("admin-step-up/totp", { code });
      } else {
        const optionsJSON = passkeyOptionsSchema.parse(
          await nativeAuthRequest("admin-step-up/passkey/options", {}),
        );
        const response = await startAuthentication({ optionsJSON });
        result = await nativeAuthRequest("admin-step-up/passkey/verify", {
          response,
        });
      }
      onConfirmed(
        confirmationSchema.parse(result).adminStepUpExpiresIn,
        generation,
      );
    } catch (error) {
      if (error instanceof NativeAuthRequestError && error.code === "FORBIDDEN")
        onForbidden();
      else setError(failureMessage(error));
    } finally {
      setBusy(false);
      setCode("");
    }
  };
  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent showCloseButton={false}>
        <DialogTitle>Confirm it's you</DialogTitle>
        <DialogDescription>
          Confirm your identity to access admin pages.
        </DialogDescription>
        {session.hasPasskey && (
          <Button disabled={busy} onClick={() => void confirm("passkey")}>
            Use passkey
          </Button>
        )}
        {session.hasTotp && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void confirm("totp");
            }}
            className="grid gap-3"
          >
            <label htmlFor="admin-totp">Authenticator code</label>
            <Input
              id="admin-totp"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              required
              disabled={busy}
            />
            <Button type="submit" disabled={busy || !/^\d{6}$/.test(code)}>
              Confirm code
            </Button>
          </form>
        )}
        {!session.hasPasskey && !session.hasTotp && (
          <Link to="/settings" hash="security">
            Settings → Security
          </Link>
        )}
        {(error || lockError) && <p role="alert">{error || lockError}</p>}
        <Button variant="outline" disabled={busy} onClick={onLeave}>
          Go home
        </Button>
      </DialogContent>
    </Dialog>
  );
}
